/** Building and digging: edit targets, the shared edit rules, and finding things within reach. */
import { CHARACTER, PLANTS, SHIP } from "../constants";
import { boxesOverlap, v2, vDist, vDot, vSub, type Box, type Vec2 } from "../math";
import { T, TILES, blocksShips, cellShape, cellTile, digTimeWith, inGrid, isBuilt, isMachine, isPartOf, isSolid, pieceOf, pieceParts, takesShapes, tileAt, turnAt, underPiece, type HeldTool, type PieceDef, type TileId } from "../tiles";
import { isShape } from "../shapes";
import type { ShipPart } from "../blocks";
import { type PlayerState } from "./types";
import { type AsteroidBody, type ShipBody, type Sim, shipToWorld, worldToShip, shipCenter } from "./ships";
import { asteroidPlants, plantAt } from "./rocks";
import { upVector, rightVector, helmUnder } from "./character";
import { notEmpty, circleHitsGrid, circleHitsBox } from "./collision";
import { seatHere } from "./turrets";

// ----------------------------------------------------------------- building

/**
 * Where a build or dig lands: a tile in a ship's grid, in an asteroid's grid,
 * or in open space. A "space" target is a world tile (x, y) with no id; a
 * block built there is a new one-block ship that floats off.
 */
export interface EditTarget {
  kind: "ship" | "asteroid" | "space";
  id: string;
  x: number;
  y: number;
}

/** Would a block at ship tile (x, y) attach to the ship: is it touching one of its blocks? A ship with none left takes anything. */
export function attachesToShip(ship: ShipBody, x: number, y: number): boolean {
  if (ship.stats.cells.length === 0) return true;
  return [[1, 0], [-1, 0], [0, 1], [0, -1]].some(([dx, dy]) => tileAt(ship.grid, x + dx!, y + dy!) !== T.EMPTY);
}

/** The ship tile under a world point if it's one you could dig or build onto: one of its blocks, or a spot touching them. */
export function shipCellFor(ship: ShipBody, wx: number, wy: number): { x: number; y: number } | null {
  const local = worldToShip(ship, v2(wx, wy));
  const x = Math.floor(local.x);
  const y = Math.floor(local.y);
  if (!inGrid(ship.grid, x, y)) return null;
  return tileAt(ship.grid, x, y) !== T.EMPTY || attachesToShip(ship, x, y) ? { x, y } : null;
}

/**
 * Which tile the cursor is over: a ship's blocks and the spots touching them
 * (the ship you're aboard, `aboardId`, wins where ships overlap), then the
 * asteroid whose grid contains the point, and failing both, open space.
 */
export function editTargetAt(sim: Sim, wx: number, wy: number, aboardId: string | null | undefined): EditTarget {
  const aboard = aboardId ? sim.ships.get(aboardId) : undefined;
  const onAboard = aboard && shipCellFor(aboard, wx, wy);
  if (aboard && onAboard) return { kind: "ship", id: aboard.state.id, ...onAboard };
  for (const ship of sim.ships.values()) {
    if (vDist(v2(wx, wy), shipCenter(ship)) > ship.stats.radius + 1.5) continue;
    const cell = shipCellFor(ship, wx, wy);
    if (cell) return { kind: "ship", id: ship.state.id, ...cell };
  }
  // Grids can overlap where rocks sit close (a field, a sun's belts): the one with something at the point, else the nearest.
  let near: EditTarget | null = null;
  let nearDist = Infinity;
  for (const a of sim.asteroids) {
    const x = Math.floor(wx - a.def.ox);
    const y = Math.floor(wy - a.def.oy);
    if (!inGrid(a.grid, x, y)) continue;
    if (a.grid.tiles[y * a.grid.w + x] !== T.EMPTY) return { kind: "asteroid", id: a.def.id, x, y };
    const d = Math.hypot(wx - a.def.ox - a.def.w / 2, wy - a.def.oy - a.def.h / 2);
    if (d < nearDist) [near, nearDist] = [{ kind: "asteroid", id: a.def.id, x, y }, d];
  }
  return near ?? { kind: "space", id: "", x: Math.floor(wx), y: Math.floor(wy) };
}

/**
 * Validate placing `cell` (a tile and its shape, see packCell; or digging,
 * for EMPTY) at `t`. Returns null when allowed, otherwise a short reason. Shared so the client can show the same
 * verdict the server will reach. `reachFrom` is the middle of the body and
 * `up` the world angle its head points (see playerWorldUp): building reaches
 * CHARACTER.reach from the middle, digging only CHARACTER.digReach from the body.
 */
export function checkEdit(sim: Sim, t: EditTarget, cell: number, reachFrom: Vec2, occupied: Box[], up = 0): string | null {
  // The cell is the tile and its shape (see packCell): only a block that takes shapes may have one.
  const tile = cellTile(cell);
  const shape = cellShape(cell);
  if (tile !== T.EMPTY && !TILES[tile]?.buildable) return "can't place that";
  if (shape && (!takesShapes(tile) || !isShape(shape))) return "can't place that";
  // A sapling takes root only beside grass or moss.
  if (TILES[tile]?.item === "sapling") {
    const a = t.kind === "asteroid" ? sim.asteroids.find((x) => x.def.id === t.id) : undefined;
    const soil = (x: number, y: number) => {
      const s = a ? tileAt(a.grid, x, y) : T.EMPTY;
      return s === T.GRASS || s === T.MOSS;
    };
    if (!a || !(soil(t.x + 1, t.y) || soil(t.x - 1, t.y) || soil(t.x, t.y + 1) || soil(t.x, t.y - 1))) return "saplings take root in grass or moss";
  }
  // A piece goes down whole, by its anchor (bottom-left); its other parts can't be placed alone.
  const piece = pieceOf(tile);
  if (piece && piece.def.anchor === tile) return checkPiece(sim, t, piece.def, reachFrom, occupied);
  if (piece) return "can't place that";
  const ship = t.kind === "ship" ? sim.ships.get(t.id) : undefined;
  const asteroid = t.kind === "asteroid" ? sim.asteroids.find((a) => a.def.id === t.id) : undefined;
  let center: Vec2;
  let current: TileId;
  if (t.kind === "space") {
    // Open space only: inside an asteroid's grid, blocks belong to the asteroid.
    if (sim.asteroids.some((a) => inGrid(a.grid, t.x - a.def.ox, t.y - a.def.oy))) return "nothing there";
    center = v2(t.x + 0.5, t.y + 0.5);
    current = T.EMPTY;
  } else {
    const grid = ship?.grid ?? asteroid?.grid;
    if (!grid || !inGrid(grid, t.x, t.y)) return "nothing there";
    center = ship ? shipToWorld(ship, v2(t.x + 0.5, t.y + 0.5)) : v2(asteroid!.def.ox + t.x + 0.5, asteroid!.def.oy + t.y + 0.5);
    current = tileAt(grid, t.x, t.y);
  }
  if (tile === T.EMPTY) {
    if (current === T.EMPTY) return "nothing to remove";
    return digDistance(reachFrom, center, up) > CHARACTER.digReach ? "too far to dig" : null;
  }
  if (vDist(center, reachFrom) > CHARACTER.reach) return "out of reach";
  // Building into water just displaces it.
  if (current !== T.EMPTY && current !== T.WATER) return "occupied";

  if (ship) {
    // A ship tile may be turned, so test it as a circle.
    if (isSolid(tile) && occupied.some((b) => circleHitsBox(center.x, center.y, 0.45, b))) return "someone is standing there";
    for (const a of sim.asteroids) {
      if (circleHitsGrid(center.x, center.y, SHIP.cellRadius, a.grid, a.def.ox, a.def.oy, blocksShips)) return "blocked by rock";
    }
    if (!attachesToShip(ship, t.x, t.y)) return "not touching the ship";
  } else {
    const cell = { x0: center.x - 0.5, y0: center.y - 0.5, x1: center.x + 0.5, y1: center.y + 0.5 };
    if (isSolid(tile) && occupied.some((b) => boxesOverlap(b, cell))) return "someone is standing there";
    for (const other of sim.ships.values()) {
      if (vDist(center, shipCenter(other)) > other.stats.radius + 1) continue;
      const local = worldToShip(other, center);
      // Any ship tile whose collision circle would reach into this cell.
      if (circleHitsGrid(local.x, local.y, SHIP.cellRadius + 0.5, other.grid, 0, 0, notEmpty)) return "blocked by a ship";
    }
  }
  return null;
}

/**
 * The nearest tile of kind `tile` (or part of a piece of that kind; or of
 * ship machine `tile`, in any family) within `reach` of world point `point`,
 * on any ship or asteroid (`ship` is set when it's on a ship). Used to find a
 * workbench to craft at, or a furnace to stoke.
 */
export function tileNear(sim: Sim, point: Vec2, tile: TileId | ShipPart, reach: number): { ship?: ShipBody; at: Vec2 } | null {
  let best: { ship?: ShipBody; at: Vec2 } | null = null;
  let bestD = reach;
  const matches = (t: TileId) => (typeof tile === "string" ? isMachine(t, tile) : t === tile || isPartOf(t, tile));
  const consider = (at: Vec2, ship?: ShipBody) => {
    const d = vDist(at, point);
    if (d <= bestD) [best, bestD] = [{ ship, at }, d];
  };
  const r = Math.ceil(reach) + 1;
  for (const ship of sim.ships.values()) {
    if (vDist(point, shipCenter(ship)) > ship.stats.radius + reach) continue;
    const local = worldToShip(ship, point);
    for (let y = Math.floor(local.y) - r; y <= Math.floor(local.y) + r; y++) {
      for (let x = Math.floor(local.x) - r; x <= Math.floor(local.x) + r; x++) {
        if (matches(tileAt(ship.grid, x, y))) consider(shipToWorld(ship, v2(x + 0.5, y + 0.5)), ship);
      }
    }
  }
  for (const a of sim.asteroids) {
    const cx = Math.floor(point.x - a.def.ox);
    const cy = Math.floor(point.y - a.def.oy);
    if (cx < -r || cy < -r || cx >= a.grid.w + r || cy >= a.grid.h + r) continue;
    for (let y = cy - r; y <= cy + r; y++) {
      for (let x = cx - r; x <= cx + r; x++) {
        if (matches(tileAt(a.grid, x, y))) consider(v2(a.def.ox + x + 0.5, a.def.oy + y + 0.5));
      }
    }
  }
  return best;
}

/** What swinging at an edit target hits: its tile (EMPTY for open space). */
export function editTargetTile(sim: Sim, t: EditTarget): TileId {
  const grid = t.kind === "ship" ? sim.ships.get(t.id)?.grid : t.kind === "asteroid" ? sim.asteroids.find((x) => x.def.id === t.id)?.grid : undefined;
  return grid ? tileAt(grid, t.x, t.y) : T.EMPTY;
}

/** The plant the tile at an edit target is part of, if it's on an asteroid and still there: its asteroid and number. */
export function plantAtTarget(sim: Sim, t: EditTarget): { a: AsteroidBody; plant: number } | null {
  if (t.kind !== "asteroid") return null;
  const a = sim.asteroids.find((x) => x.def.id === t.id);
  if (!a || !inGrid(a.grid, t.x, t.y) || a.grid.tiles[t.y * a.grid.w + t.x] === T.EMPTY) return null;
  const plant = plantAt(a.def, t.y * a.grid.w + t.x);
  return plant === null ? null : { a, plant };
}

/** Seconds of swinging `tool` it takes to break what's at an edit target (a whole plant, for any part of one), or null if it can't be done. */
export function digTimeAt(sim: Sim, t: EditTarget, tool: HeldTool | null): number | null {
  const at = plantAtTarget(sim, t);
  if (at) {
    const plant = asteroidPlants(at.a.def)[at.plant]!;
    const time = digTimeWith(plant.grown[0]!, tool);
    return time === null ? null : time * PLANTS.kinds[plant.kind].chop;
  }
  return digTimeWith(editTargetTile(sim, t), tool);
}

/** The world angle a player's head points: their own `up`, turned with the ship they're aboard. */
export function playerWorldUp(p: PlayerState, sim: Sim): number {
  const ship = p.frame ? sim.ships.get(p.frame) : undefined;
  return (ship?.state.angle ?? 0) + p.up;
}

/**
 * How far `point` is from the edge of a character's body (centred on `center`,
 * head toward `up`), in tiles. Digging reaches CHARACTER.digReach of this.
 */
export function digDistance(center: Vec2, point: Vec2, up: number): number {
  const rel = vSub(point, center);
  const along = Math.abs(vDot(rel, upVector(up))) - CHARACTER.height / 2;
  const side = Math.abs(vDot(rel, rightVector(up))) - CHARACTER.width / 2;
  return Math.hypot(Math.max(0, along), Math.max(0, side));
}

/**
 * Placing piece `def` with its anchor at `t`, on a ship or (unless it's
 * ship-only) an asteroid: every tile in reach and clear of anyone standing
 * there, and either all empty or all back wall (it goes in front, and leaves
 * them when it goes). On a ship it must touch the hull and keep clear of rock;
 * on an asteroid, clear of ships.
 */
export function checkPiece(sim: Sim, t: EditTarget, def: PieceDef, reachFrom: Vec2, occupied: Box[]): string | null {
  const name = TILES[def.anchor]!.name.toLowerCase();
  const ship = t.kind === "ship" ? sim.ships.get(t.id) : undefined;
  const asteroid = t.kind === "asteroid" && !def.shipOnly ? sim.asteroids.find((a) => a.def.id === t.id) : undefined;
  const grid = ship?.grid ?? asteroid?.grid;
  if (!grid) return def.shipOnly ? `${name}s go on ships` : `${name}s go on ships or asteroids`;
  const backed = underPiece(tileAt(grid, t.x, t.y));
  let attached = false;
  // Turned as the grid turns there (see turnAt): upright to whoever stands on that part of the rock.
  for (const [dx, dy, part] of pieceParts(def.anchor, turnAt(grid, t.x, t.y))) {
    const x = t.x + dx;
    const y = t.y + dy;
    const there = tileAt(grid, x, y);
    if (!inGrid(grid, x, y) || (there !== T.EMPTY && !underPiece(there))) return `no room for a ${name}`;
    if (underPiece(there) !== backed) return "half on a back wall";
    const c = ship ? shipToWorld(ship, v2(x + 0.5, y + 0.5)) : v2(asteroid!.def.ox + x + 0.5, asteroid!.def.oy + y + 0.5);
    if (vDist(c, reachFrom) > CHARACTER.reach) return "out of reach";
    if (isSolid(part) && occupied.some((b) => circleHitsBox(c.x, c.y, 0.45, b))) return "someone is standing there";
    if (ship) {
      for (const a of sim.asteroids) {
        if (circleHitsGrid(c.x, c.y, SHIP.cellRadius, a.grid, a.def.ox, a.def.oy, blocksShips)) return "blocked by rock";
      }
      attached ||= backed || attachesToShip(ship, x, y);
    } else {
      for (const other of sim.ships.values()) {
        if (vDist(c, shipCenter(other)) > other.stats.radius + 1) continue;
        const local = worldToShip(other, c);
        if (circleHitsGrid(local.x, local.y, SHIP.cellRadius + 0.5, other.grid, 0, 0, notEmpty)) return "blocked by a ship";
      }
    }
  }
  return !ship || attached ? null : "not touching the ship";
}

/** The world point at the middle of an edit target's tile (or null if its ship or asteroid is gone). */
export function editTargetCenter(sim: Sim, t: EditTarget): Vec2 | null {
  const local = v2(t.x + 0.5, t.y + 0.5);
  if (t.kind === "space") return local;
  if (t.kind === "ship") {
    const ship = sim.ships.get(t.id);
    return ship ? shipToWorld(ship, local) : null;
  }
  const a = sim.asteroids.find((x) => x.def.id === t.id);
  return a ? v2(a.def.ox + local.x, a.def.oy + local.y) : null;
}

/** Would E do something with a helm or turret for `p` right now (take, leave or climb in)? */
export function atHelmOrTurret(p: PlayerState, sim: Sim): boolean {
  if (p.piloting || p.gunning !== null) return true;
  const ship = p.frame ? sim.ships.get(p.frame) : undefined;
  return (!!ship && helmUnder(p, ship)) || seatHere(p, sim) !== null;
}
